<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { Send, Sparkles } from 'lucide-vue-next';
import type { Thread } from '../services/api';
import { threadsAPI, type Message } from '../services/api';
import echo from '../services/echo';
import { isThreadTaken } from '../utils/thread';

interface Props {
  conversation: Thread;
  isAiTyping: boolean;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  updateConversation: [conversation: Thread];
}>();

const { t } = useI18n();

const message = ref('');
const messages = ref<Message[]>([]);
const isLoading = ref(false);
const isSending = ref(false);
const isTakingOver = ref(false);
const takeOverError = ref<string | null>(null);
const currentPage = ref(1);
const hasMore = ref(true);
const perPage = 10;
const scrollContainer = ref<HTMLDivElement | null>(null);

const formatMessageTime = (dateStr: string): string => {
  const date = new Date(dateStr);
  const now = new Date();
  const time = date.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);

  if (date.toDateString() === now.toDateString()) return time;
  if (date.toDateString() === yesterday.toDateString())
    return t('chat_window.time_yesterday', { time });

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  if (date.getFullYear() === now.getFullYear()) return `${day}.${month} ${time}`;
  return `${day}.${month}.${date.getFullYear()} ${time}`;
};

const isThreadDone = computed(() => props.conversation.status === 'done');

// The reply box only opens while the conversation is taken. Read once per change of
// the thread, not on a clock: the box stays as it was until the next update either way.
const isConversationTaken = computed(() => isThreadTaken(props.conversation, Date.now()));

const loadMessages = async (threadId: string, page: number = 1) => {
  if (isLoading.value) return;

  isLoading.value = true;
  try {
    const response = await threadsAPI.getMessages(threadId, {
      page,
      per_page: perPage,
    });

    // Reverse messages so newest are at the bottom
    const reversedMessages = [...response.messages].reverse();

    if (page === 1) {
      // First load - replace all messages
      messages.value = reversedMessages;
      // Scroll to bottom after loading
      setTimeout(() => {
        if (scrollContainer.value) {
          scrollContainer.value.scrollTop = scrollContainer.value.scrollHeight;
        }
      }, 100);
    } else {
      // Load more - prepend to existing (older messages go on top)
      const oldScrollHeight = scrollContainer.value?.scrollHeight || 0;
      messages.value = [...reversedMessages, ...messages.value];

      // Maintain scroll position
      setTimeout(() => {
        if (scrollContainer.value) {
          const newScrollHeight = scrollContainer.value.scrollHeight;
          scrollContainer.value.scrollTop = newScrollHeight - oldScrollHeight;
        }
      }, 0);
    }

    hasMore.value = response.has_more;
    currentPage.value = page;
  } catch (error: any) {
    console.error('Error loading messages:', error);
  } finally {
    isLoading.value = false;
  }
};

const handleScroll = () => {
  if (!scrollContainer.value || !hasMore.value || isLoading.value) return;

  // Load more when scrolled near top (for older messages)
  const threshold = 100;
  if (scrollContainer.value.scrollTop <= threshold) {
    loadMessages(props.conversation.id, currentPage.value + 1);
  }
};

const handleSend = async (e: Event) => {
  e.preventDefault();

  // Don't send if conversation is not taken or message is empty
  if (!isConversationTaken.value || !message.value.trim() || isSending.value) {
    return;
  }

  const messageText = message.value.trim();
  isSending.value = true;

  try {
    await threadsAPI.sendMessage(props.conversation.id, messageText);
    message.value = '';
  } catch (error: any) {
    console.error('Error sending message:', error);
    takeOverError.value = t('chat_window.error_send_message', {
      error: error.response?.data?.message || t('common.error_generic'),
    });
  } finally {
    isSending.value = false;
  }
};

const handleTakeOver = async () => {
  if (isTakingOver.value || isThreadDone.value) return;

  isTakingOver.value = true;
  takeOverError.value = null;

  try {
    let updatedThreadData;

    if (isConversationTaken.value) {
      // Release conversation back to AI
      updatedThreadData = await threadsAPI.release(props.conversation.id);
    } else {
      // Take over conversation
      updatedThreadData = await threadsAPI.takeOver(props.conversation.id);
    }

    // Update conversation with new taken_until value
    const updatedConversation: Thread = {
      ...props.conversation,
      takenUntil: updatedThreadData.taken_until,
    };

    emit('updateConversation', updatedConversation);
  } catch (err: any) {
    console.error('Error updating conversation:', err);
    takeOverError.value = err.response?.data?.message || t('chat_window.error_update_conversation');
  } finally {
    isTakingOver.value = false;
  }
};

// Setup Echo channel for real-time messages
const setupEchoChannel = (threadId: string) => {
  // Leave previous channel if exists
  if (currentChannel) {
    echo.leave(currentChannel);
  }

  // Join new private channel
  const channelName = `thread-${threadId}`;
  currentChannel = channelName;

  echo
    .private(channelName)
    .listen('.message.created', (data: any) => {
      console.log('New message received:', data);

      // Add new message to the list
      const newMessage: Message = {
        id: data.id,
        text: data.message,
        sender: data.is_ai ? 'ai' : data.client_id ? 'customer' : 'user',
        timestamp: data.created_at,
      };

      messages.value.push(newMessage);

      // Auto-scroll to bottom
      setTimeout(() => {
        if (scrollContainer.value) {
          scrollContainer.value.scrollTop = scrollContainer.value.scrollHeight;
        }
      }, 100);
    })
    .error((error: any) => {
      console.error('Echo channel error:', error);
    });
};

let currentChannel: string | null = null;

// Watch for conversation changes
watch(
  () => props.conversation.id,
  (newId) => {
    if (newId) {
      currentPage.value = 1;
      hasMore.value = true;
      loadMessages(newId, 1);
      setupEchoChannel(newId);
    }
  },
  { immediate: true },
);

onMounted(() => {
  if (scrollContainer.value) {
    scrollContainer.value.addEventListener('scroll', handleScroll);
  }
});

onUnmounted(() => {
  if (scrollContainer.value) {
    scrollContainer.value.removeEventListener('scroll', handleScroll);
  }

  // Leave Echo channel on unmount
  if (currentChannel) {
    echo.leave(currentChannel);
  }
});
</script>

<template>
  <div class="flex-1 min-h-0 flex flex-col bg-[#0A0A0B]">
    <!-- Header -->
    <div class="px-4 md:px-8 py-4 md:py-5 border-b border-[#2A2018]">
      <div class="flex items-center justify-between gap-4">
        <h2 class="text-lg text-[#F5F0EA] flex-shrink-0">
          {{ conversation.name }}
        </h2>

        <!-- Mobile/Tablet Status & Take Over Button (Hidden on xl screens) -->
        <div class="xl:hidden flex items-center gap-3">
          <!-- Status Badge -->
          <div class="flex items-center gap-2">
            <div
              :class="[
                'w-2 h-2 rounded-full',
                isConversationTaken ? 'bg-blue-500' : 'bg-green-500',
              ]"
            />
            <span class="text-xs text-[#9E9080] hidden sm:inline">
              {{
                isConversationTaken ? $t('chat_window.status_taken') : $t('chat_window.status_ai')
              }}
            </span>
          </div>

          <!-- Take Over Button -->
          <button
            @click="handleTakeOver"
            :disabled="isTakingOver || isThreadDone"
            :class="[
              'px-3 py-1.5 rounded-xl transition text-xs flex items-center gap-1.5 whitespace-nowrap',
              isConversationTaken
                ? 'bg-blue-900/40 hover:bg-blue-900/60 text-blue-300 disabled:bg-blue-900/20'
                : 'bg-[#1E1E23] hover:bg-[#2A2018] text-[#9E9080] disabled:bg-[#111113]',
              isTakingOver || isThreadDone ? 'opacity-50 cursor-not-allowed' : '',
            ]"
          >
            <div
              v-if="isTakingOver"
              class="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin"
            ></div>
            <span v-else>{{
              isConversationTaken ? $t('chat_window.release') : $t('chat_window.take_over')
            }}</span>
          </button>
        </div>
      </div>

      <!-- Error Message (Mobile/Tablet) -->
      <div
        v-if="takeOverError && !isTakingOver"
        class="xl:hidden mt-2 p-2 bg-red-900/20 border border-red-800 rounded-lg"
      >
        <p class="text-xs text-red-400">{{ takeOverError }}</p>
      </div>
    </div>

    <!-- Messages Area -->
    <div ref="scrollContainer" class="flex-1 min-h-0 overflow-y-auto px-8 py-6 space-y-4">
      <!-- Loading Indicator for older messages -->
      <div v-if="isLoading && currentPage > 1" class="text-center py-4">
        <div
          class="inline-block w-6 h-6 border-2 border-[#2A2018] border-t-[#F5F0EA] rounded-full animate-spin"
        ></div>
      </div>

      <!-- Empty state -->
      <div
        v-if="!isLoading && messages.length === 0"
        class="flex items-center justify-center h-full"
      >
        <p class="text-sm text-[#524840]">{{ $t('chat_window.empty') }}</p>
      </div>

      <!-- Messages -->
      <div
        v-for="msg in messages"
        :key="msg.id"
        :class="[
          'flex gap-2 items-start',
          msg.sender === 'customer' ? 'justify-end' : 'justify-start',
        ]"
      >
        <!-- AI Icon (shown before message bubble) -->
        <div
          v-if="msg.sender === 'ai'"
          class="flex-shrink-0 w-8 h-8 rounded-full bg-[#D4956A] flex items-center justify-center mt-1"
        >
          <Sparkles class="w-4 h-4 text-[#F5F0EA]" />
        </div>

        <div class="flex flex-col" :class="msg.sender === 'customer' ? 'items-end' : 'items-start'">
          <div
            :class="[
              'max-w-md px-4 py-2.5 rounded-2xl text-[#F5F0EA]',
              msg.sender === 'ai' ? 'font-mono' : '',
            ]"
            :style="
              msg.sender === 'customer'
                ? {
                    background: '#1E1E23',
                    borderBottomRightRadius: '4px',
                    fontSize: '0.8rem',
                    lineHeight: '1.5',
                  }
                : msg.sender === 'ai'
                  ? {
                      background: 'rgba(212, 149, 106, 0.14)',
                      border: '1px solid rgba(212, 149, 106, 0.22)',
                      borderBottomLeftRadius: '4px',
                      fontSize: '0.78rem',
                      lineHeight: '1.55',
                    }
                  : {
                      background: '#1E1E23',
                      borderBottomLeftRadius: '4px',
                      fontSize: '0.8rem',
                      lineHeight: '1.5',
                    }
            "
          >
            <p>{{ msg.text }}</p>
          </div>
          <span class="text-xs text-[#9E9080] mt-1 px-1">{{
            formatMessageTime(msg.timestamp)
          }}</span>
        </div>
      </div>

      <!-- AI Typing Indicator -->
      <div v-if="isAiTyping" class="flex justify-start">
        <div
          class="px-4 py-2.5 rounded-2xl"
          style="
            background: rgba(212, 149, 106, 0.14);
            border: 1px solid rgba(212, 149, 106, 0.22);
            border-bottom-left-radius: 4px;
          "
        >
          <p class="text-sm text-[#524840]">{{ $t('chat_window.ai_typing') }}</p>
        </div>
      </div>

      <!-- Loading Indicator for initial load -->
      <div v-if="isLoading && currentPage === 1" class="flex items-center justify-center h-full">
        <div
          class="inline-block w-8 h-8 border-2 border-[#2A2018] border-t-[#F5F0EA] rounded-full animate-spin"
        ></div>
      </div>
    </div>

    <!-- Message Input -->
    <div class="px-8 py-5 border-t border-[#2A2018]">
      <form @submit="handleSend" class="flex flex-col gap-2">
        <!-- Warning when conversation is not taken -->
        <p v-if="!isConversationTaken" class="text-xs text-[#524840] px-1">
          {{ $t('chat_window.take_over_warning') }}
        </p>

        <div class="flex gap-3">
          <input
            type="text"
            v-model="message"
            :disabled="!isConversationTaken || isSending"
            :placeholder="
              isConversationTaken
                ? $t('chat_window.placeholder_taken')
                : $t('chat_window.placeholder_ai')
            "
            :class="[
              'flex-1 px-4 py-3 border-0 rounded-3xl outline-none transition text-sm',
              isConversationTaken
                ? 'bg-[#1E1E23] focus:ring-2 focus:ring-[#D4956A] focus:bg-[#1E1E23] text-[#F5F0EA] placeholder:text-[#9E9080]'
                : 'bg-[#111113] text-[#9E9080] placeholder:text-[#9E9080] cursor-not-allowed',
            ]"
          />
          <button
            type="submit"
            :disabled="!isConversationTaken || !message.trim() || isSending"
            class="px-5 py-3 bg-[#1E1E23] text-[#F5F0EA] rounded-3xl hover:bg-[#1E1E23] disabled:bg-[#1E1E23] disabled:text-[#9E9080] disabled:cursor-not-allowed transition flex items-center justify-center"
          >
            <div
              v-if="isSending"
              class="w-4 h-4 border-2 border-[#2A2018] border-t-transparent rounded-full animate-spin"
            ></div>
            <Send v-else class="w-4 h-4" />
          </button>
        </div>
      </form>
    </div>
  </div>
</template>
